import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit2, Trash2, Search, ClipboardList, CheckCircle, X, Camera, Eye, GitBranch, Archive, ArchiveRestore, Printer } from 'lucide-react';
import { PageHeader, KpiCard, STREAMS } from '../components/PageHeader';
import { toast } from 'sonner';
import QRScanner from '../components/QRScanner';
import { openBrandedPrintWindow, PrintHelpers as H } from '../utils/brandedPrint';

const BOMsPage = ({ level }) => {
  const { api, hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [boms, setBoms] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    product_type_id: '',
    bom_variant_code: '',
    bom_revision: '1.0',
    status: 'Draft',
    notes: '',
    description: '',
    developed_by: '',
    next_revision_date: '',
    require_matched_file_to_release: false,
    lines: [],
    // iter76 — optional engineering linked file attached during BOM creation
    initial_linked_file: { label: '', file_type: 'CAD', url: '', revision: '' },
  });
  const [newLine, setNewLine] = useState({
    component_sku: '',
    quantity_per_unit: 1,
    uom: 'EA',
    scrap_factor_percent: 0,
    substitutes_allowed: false,
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [reviseTarget, setReviseTarget] = useState(null);
  const [reviseNote, setReviseNote] = useState('');
  const [reviseBusy, setReviseBusy] = useState(false);
  // iter77 — linked-file template picker for the "attach during create" section
  const [linkedFileTemplates, setLinkedFileTemplates] = useState([]);
  const [pickedLinkedTplId, setPickedLinkedTplId] = useState('');

  const fetchData = async () => {
    try {
      const [bomsRes, productTypesRes, itemsRes] = await Promise.all([
        api().get(`/boms?include_archived=${showArchived ? 'true' : 'false'}`),
        api().get('/product-types'),
        api().get('/items')
      ]);
      setBoms(bomsRes.data);
      setProductTypes(productTypesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  // iter77 — fetch linked-file templates for the BOM create dialog
  useEffect(() => {
    if (!dialogOpen || editingItem) return;
    api().get('/bom-linked-file-templates').then(r => setLinkedFileTemplates(r.data || [])).catch(() => setLinkedFileTemplates([]));
    setPickedLinkedTplId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, editingItem]);

  const applyLinkedFileTemplate = async (tplId) => {
    setPickedLinkedTplId(tplId);
    if (!tplId) return;
    try {
      const { data } = await api().post(`/bom-linked-file-templates/${tplId}/preview`, {
        bom_variant_code: formData.bom_variant_code,
        revision: formData.bom_revision,
        product_type: (productTypes.find(p => p.id === formData.product_type_id) || {}).product_type_name || '',
      });
      setFormData(s => ({
        ...s,
        initial_linked_file: {
          ...s.initial_linked_file,
          file_type: data.file_type || s.initial_linked_file.file_type,
          label: data.label || s.initial_linked_file.label,
          url: data.url || s.initial_linked_file.url,
          revision: data.revision || s.initial_linked_file.revision,
        },
      }));
      toast.success('Template applied — review the fields before submitting');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not apply template'); }
  };

  const handleRevise = async () => {
    if (!reviseTarget) return;
    setReviseBusy(true);
    try {
      const { data } = await api().post(`/boms/${reviseTarget.id}/revise`, {
        notes: reviseNote || undefined,
      });
      toast.success(`Revised → ${data.bom_variant_code} rev ${data.bom_revision}`);
      setReviseTarget(null);
      setReviseNote('');
      fetchData();
      navigate(`/boms/view/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revise failed');
    } finally {
      setReviseBusy(false);
    }
  };

  const handleArchive = async (bom) => {
    if (!window.confirm(`Archive ${bom.bom_variant_code} rev ${bom.bom_revision}? It will be hidden from the default list.`)) return;
    try {
      await api().post(`/boms/${bom.id}/archive`);
      toast.success('Archived');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };

  const handleUnarchive = async (bom) => {
    try {
      await api().post(`/boms/${bom.id}/unarchive`);
      toast.success('Unarchived');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unarchive failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // iter76 — strip non-BOM fields before submit
      const { initial_linked_file, ...bomFields } = formData;
      const payload = {
        ...bomFields,
        lines: formData.lines.map(line => ({
          component_sku: line.component_sku,
          quantity_per_unit: parseFloat(line.quantity_per_unit),
          uom: line.uom,
          scrap_factor_percent: parseFloat(line.scrap_factor_percent) || 0,
          substitutes_allowed: line.substitutes_allowed,
        })),
      };
      if (editingItem) {
        await api().put(`/boms/${editingItem.id}`, payload);
        toast.success('BOM updated');
      } else {
        const res = await api().post('/boms', payload);
        const saved = res.data || {};
        const enteredRev = (payload.bom_revision || '1.0').toString();
        const savedRev = (saved.bom_revision || enteredRev).toString();
        if (savedRev !== enteredRev) {
          toast.success(`BOM created · ${saved.bom_variant_code} rev ${savedRev} (auto-bumped from ${enteredRev} — variant already existed)`);
        } else {
          toast.success(`BOM created · ${saved.bom_variant_code} rev ${savedRev}`);
        }
        // iter76 — attach initial engineering linked file (if user filled it in)
        const lf = initial_linked_file || {};
        if (saved.id && lf.label && lf.label.trim() && (lf.url || '').trim()) {
          let cleanUrl = lf.url.trim();
          const lower = cleanUrl.toLowerCase();
          if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
            if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(cleanUrl)) {
              cleanUrl = 'https://' + cleanUrl;
            }
          }
          try {
            await api().post(`/boms/${saved.id}/linked-files`, {
              label: lf.label.trim(),
              file_type: lf.file_type || 'CAD',
              url: cleanUrl,
              revision: lf.revision || undefined,
            });
            toast.success('Engineering linked file attached');
          } catch (e2) {
            toast.error('BOM saved, but linked file failed: ' + (e2.response?.data?.detail || 'error'));
          }
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleRelease = async (id) => {
    if (!window.confirm('Release this BOM? Once released, it cannot be modified while in use.')) return;
    try {
      await api().put(`/boms/${id}/release`);
      toast.success('BOM released');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Release failed';
      // iter74 — engineering match gate offers Admin override
      if (typeof msg === 'string' && msg.startsWith('Engineering match required') && user?.role === 'Admin') {
        const reason = window.prompt(
          'This BOM requires an engineer-matched linked file before release.\n\nProvide a reason to override and release anyway (audited):',
          ''
        );
        if (reason === null) return; // cancelled
        try {
          await api().put(`/boms/${id}/release?admin_override=true&override_reason=${encodeURIComponent(reason || '')}`);
          toast.success('Released with Admin override (audited)');
          fetchData();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Override failed');
        }
        return;
      }
      toast.error(msg);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      product_type_id: item.product_type_id,
      bom_variant_code: item.bom_variant_code,
      bom_revision: item.bom_revision,
      status: item.status,
      notes: item.notes || '',
      description: item.description || '',
      developed_by: item.developed_by || '',
      next_revision_date: item.next_revision_date || '',
      require_matched_file_to_release: !!item.require_matched_file_to_release,
      lines: item.lines || [],
      initial_linked_file: { label: '', file_type: 'CAD', url: '', revision: '' },
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this BOM?')) return;
    try {
      await api().delete(`/boms/${id}`);
      toast.success('BOM deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const addLine = () => {
    if (!newLine.component_sku) return;
    setFormData({
      ...formData,
      lines: [...formData.lines, { ...newLine, id: Date.now().toString() }],
    });
    setNewLine({
      component_sku: '',
      quantity_per_unit: 1,
      uom: 'EA',
      scrap_factor_percent: 0,
      substitutes_allowed: false,
    });
  };

  const removeLine = (lineId) => {
    setFormData({
      ...formData,
      lines: formData.lines.filter(l => l.id !== lineId),
    });
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      product_type_id: '',
      bom_variant_code: '',
      bom_revision: '1.0',
      status: 'Draft',
      notes: '',
      description: '',
      developed_by: '',
      next_revision_date: '',
      require_matched_file_to_release: false,
      lines: [],
      initial_linked_file: { label: '', file_type: 'CAD', url: '', revision: '' },
    });
  };

  const getProductTypeName = (id) => productTypes.find(p => p.id === id)?.product_type_name || '-';
  const getItemName = (sku) => items.find(i => i.item_sku === sku)?.item_name || sku;

  // Handle QR code scan result
  const handleQRScan = (scannedText) => {
    // Try to find item by SKU (scanned text might be the SKU)
    const foundItem = items.find(i => 
      i.item_sku === scannedText || 
      i.item_sku.toLowerCase() === scannedText.toLowerCase()
    );

    if (foundItem) {
      // Check if already in BOM lines
      const alreadyExists = formData.lines.some(l => l.component_sku === foundItem.item_sku);
      if (alreadyExists) {
        toast.info(`${foundItem.item_sku} is already in the BOM`);
      } else {
        // Add to BOM lines
        setFormData({
          ...formData,
          lines: [...formData.lines, {
            id: Date.now().toString(),
            component_sku: foundItem.item_sku,
            quantity_per_unit: 1,
            uom: foundItem.base_uom || 'EA',
            scrap_factor_percent: 0,
            substitutes_allowed: false,
          }],
        });
        toast.success(`Added ${foundItem.item_sku} - ${foundItem.item_name}`);
      }
    } else {
      toast.error(`Item not found: ${scannedText}`);
    }
    setScannerOpen(false);
  };

  const levelOfPt = (id) => (productTypes.find(p => p.id === id) || {}).level || 'product';
  const streamPts = productTypes.filter(pt => !level || (pt.level || 'product') === level);
  const filteredItems = boms.filter(item => (!level || levelOfPt(item.product_type_id) === level) && (
    item.bom_variant_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getProductTypeName(item.product_type_id).toLowerCase().includes(searchTerm.toLowerCase())
  ));

  // iter67 — branded printable BOM Summary report (cross-BOM rollup)
  const printBomSummary = async () => {
    // Aggregate components across all visible BOMs
    const compMap = new Map();
    let totalLines = 0;
    for (const b of filteredItems) {
      for (const ln of (b.lines || [])) {
        totalLines += 1;
        const key = ln.component_sku;
        const existing = compMap.get(key) || { sku: key, total_qty: 0, used_in: 0, uoms: new Set(), boms: [] };
        existing.total_qty += parseFloat(ln.quantity_per_unit || 0);
        existing.used_in += 1;
        existing.uoms.add(ln.uom || 'EA');
        existing.boms.push(b.bom_variant_code);
        compMap.set(key, existing);
      }
    }
    const components = Array.from(compMap.values()).sort((a, b) => b.used_in - a.used_in);

    const summary = `
      <strong>BOMs:</strong> ${filteredItems.length} &nbsp;·&nbsp;
      <strong>Total lines:</strong> ${totalLines} &nbsp;·&nbsp;
      <strong>Distinct components:</strong> ${components.length}`;

    const bomCols = [
      { key: 'bom_variant_code', label: 'Variant', mono: true },
      { key: 'product_type', label: 'Product Type', fmt: (_, r) => getProductTypeName(r.product_type_id) },
      { key: 'bom_revision', label: 'Rev', mono: true },
      { key: 'status', label: 'Status' },
      { key: 'lines', label: 'Lines', mono: true, fmt: (_, r) => (r.lines || []).length },
      { key: 'developed_by', label: 'Developed By' },
      { key: 'next_revision_date', label: 'Next Rev', fmt: (v) => v ? new Date(v).toLocaleDateString() : '—' },
    ];

    const compCols = [
      { key: 'sku', label: 'Component SKU', mono: true },
      { key: 'used_in', label: '# of BOMs', mono: true },
      { key: 'total_qty', label: 'Total Qty (sum)', mono: true, fmt: (v) => Number(v).toFixed(2) },
      { key: 'uoms', label: 'UoM(s)', fmt: (v) => Array.from(v).join(', ') },
      { key: 'boms', label: 'Used In', fmt: (v) => v.slice(0, 6).join(', ') + (v.length > 6 ? '…' : '') },
    ];

    await openBrandedPrintWindow({
      title: 'BOM SUMMARY REPORT',
      subtitle: `${filteredItems.length} BOM(s)${searchTerm ? ` · "${searchTerm}"` : ''}`,
      orientation: 'landscape',
      body: `${H.callout(summary)}
        ${H.sectionTitle('All BOMs')}
        ${H.table(bomCols, filteredItems)}
        ${H.sectionTitle('Component Roll-Up (across all BOMs)')}
        ${H.table(compCols, components.slice(0, 200))}`,
      footerNote: 'Internal Document · BOM Library',
    });
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="boms-page">
      <PageHeader
        crumbs={[{ label: 'Build' }, level ? { label: STREAMS[level].label, to: STREAMS[level].base } : { label: 'All' }, { label: 'BOMs' }]}
        title={level ? `${STREAMS[level].singular} BOMs` : 'Bill of Materials'} icon={ClipboardList} stream={level || 'neutral'}
        badge={level ? `${STREAMS[level].singular} stream` : null}
        description={level === 'assembly' ? 'Assembly BOMs list the parts (and sub-assemblies) each assembly is built from.' : level === 'product' ? 'Product BOMs combine assemblies and loose parts.' : 'Manage BOM configurations and revisions'}
        kpis={<>
          <KpiCard stream={level || 'neutral'} label="BOMs" value={filteredItems.length} testId="boms-kpi-total" />
          <KpiCard stream={level || 'neutral'} label="Released" value={filteredItems.filter(b => b.status === 'Released').length} testId="boms-kpi-released" />
          <KpiCard stream={level || 'neutral'} label="Draft" value={filteredItems.filter(b => b.status === 'Draft').length} testId="boms-kpi-draft" />
          <KpiCard stream={level || 'neutral'} label="Obsolete" value={filteredItems.filter(b => b.status === 'Obsolete').length} testId="boms-kpi-obsolete" />
        </>}
      />
      <div className="flex items-center justify-end">
        <div className="flex gap-2">
          <Button variant="outline" onClick={printBomSummary} className="rounded-sm" data-testid="bom-summary-print-btn">
            <Printer className="w-4 h-4 mr-2" /> BOM Summary Report
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-bom-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create BOM
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit BOM' : 'Create BOM'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product Type *</Label>
                  <Select value={formData.product_type_id} onValueChange={(v) => setFormData({ ...formData, product_type_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="bom-product-type-select">
                      <SelectValue placeholder="Select product type" />
                    </SelectTrigger>
                    <SelectContent>
                      {streamPts.map(pt => (
                        <SelectItem key={pt.id} value={pt.id}>{pt.product_type_name}{!level && pt.level === 'assembly' ? ' (assembly)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Variant Code *</Label>
                  <Input
                    value={formData.bom_variant_code}
                    onChange={(e) => setFormData({ ...formData, bom_variant_code: e.target.value })}
                    placeholder="e.g., STD-001"
                    required
                    className="rounded-sm mono"
                    data-testid="bom-variant-code-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Revision <span className="text-xs text-slate-400">(auto-numbered)</span></Label>
                  <Input
                    value={formData.bom_revision}
                    readOnly
                    disabled
                    placeholder="1.0 — auto"
                    className="rounded-sm mono bg-slate-50 cursor-not-allowed"
                    data-testid="bom-revision-input"
                  />
                  <p className="text-xs text-slate-500">
                    {editingItem
                      ? 'Revision cannot be changed after creation. Use "Revise" to bump.'
                      : 'Revision auto-increments when you re-use a variant code.'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })} disabled={editingItem?.status === 'Released'}>
                    <SelectTrigger className="rounded-sm" data-testid="bom-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Released">Released</SelectItem>
                      <SelectItem value="Obsolete">Obsolete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Developed By</Label>
                  <Input
                    value={formData.developed_by}
                    onChange={(e) => setFormData({ ...formData, developed_by: e.target.value })}
                    placeholder="Author / engineer name"
                    className="rounded-sm"
                    data-testid="bom-developed-by-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Revision Date</Label>
                  <Input
                    type="date"
                    value={formData.next_revision_date || ''}
                    onChange={(e) => setFormData({ ...formData, next_revision_date: e.target.value })}
                    className="rounded-sm mono"
                    data-testid="bom-next-revision-date-input"
                  />
                </div>
              </div>
              {/* iter74 — Engineering match gate */}
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.require_matched_file_to_release}
                    onChange={(e) => setFormData({ ...formData, require_matched_file_to_release: e.target.checked })}
                    className="mt-0.5 accent-emerald-600"
                    data-testid="bom-require-matched-file-checkbox"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Require engineer-matched linked file before release</span>
                    <span className="block text-[11px] text-slate-500">When ON, this BOM cannot be moved to <strong>Released</strong> status until at least one entry in the Linked Files tab is marked <strong>Matched</strong>. Admins can override with an audited reason.</span>
                  </span>
                </label>
              </div>

              {/* iter76 — Initial engineering linked file (only on create) */}
              {!editingItem && (
                <details className="border border-blue-200 rounded-sm bg-blue-50/40" data-testid="bom-initial-linked-file-section">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-blue-900 select-none">
                    + Attach engineering linked file (CAD / drawing) — optional
                  </summary>
                  <div className="p-3 pt-1 space-y-3 border-t border-blue-200">
                    <p className="text-[11px] text-slate-600">
                      Add a CAD drawing, schematic, or fabrication file now and it will be linked to this BOM the moment it&apos;s created. You can always add more files later from the BOM detail page.
                    </p>
                    {linkedFileTemplates.length > 0 && (
                      <div className="space-y-1" data-testid="bom-create-template-picker">
                        <Label className="text-xs">Start from a template (optional)</Label>
                        <Select value={pickedLinkedTplId} onValueChange={applyLinkedFileTemplate}>
                          <SelectTrigger className="rounded-sm" data-testid="bom-create-template-select"><SelectValue placeholder="Pick a saved template…" /></SelectTrigger>
                          <SelectContent>
                            {linkedFileTemplates.map(t => (
                              <SelectItem key={t.id} value={t.id} data-testid={`bom-create-template-option-${t.id}`}>
                                {t.name} <span className="text-[10px] text-slate-400 ml-2">({t.file_type})</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">File Type</Label>
                        <Select
                          value={formData.initial_linked_file.file_type}
                          onValueChange={(v) => setFormData({ ...formData, initial_linked_file: { ...formData.initial_linked_file, file_type: v } })}
                        >
                          <SelectTrigger className="rounded-sm" data-testid="bom-initial-file-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="CAD">CAD Drawing</SelectItem>
                            <SelectItem value="Schematic">Schematic / Circuit Diagram</SelectItem>
                            <SelectItem value="Weld_Drawing">Weld / Fabrication Drawing</SelectItem>
                            <SelectItem value="Photo">Photo / Reference Image</SelectItem>
                            <SelectItem value="Datasheet">Datasheet</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Revision</Label>
                        <Input
                          value={formData.initial_linked_file.revision}
                          onChange={(e) => setFormData({ ...formData, initial_linked_file: { ...formData.initial_linked_file, revision: e.target.value } })}
                          placeholder="Rev A, v3…"
                          className="rounded-sm mono"
                          data-testid="bom-initial-file-revision-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={formData.initial_linked_file.label}
                        onChange={(e) => setFormData({ ...formData, initial_linked_file: { ...formData.initial_linked_file, label: e.target.value } })}
                        placeholder="e.g., Welding Fixture Drawing"
                        className="rounded-sm"
                        data-testid="bom-initial-file-label-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">External URL (CAD server, Drive, SharePoint…)</Label>
                      <Input
                        value={formData.initial_linked_file.url}
                        onChange={(e) => setFormData({ ...formData, initial_linked_file: { ...formData.initial_linked_file, url: e.target.value } })}
                        placeholder="https://..."
                        className="rounded-sm mono text-xs"
                        data-testid="bom-initial-file-url-input"
                      />
                      <p className="text-[10px] text-slate-500">Both <em>Label</em> and <em>URL</em> are required to attach a file. Otherwise this section is skipped.</p>
                    </div>
                  </div>
                </details>
              )}
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What this BOM produces…"
                  className="rounded-sm"
                  data-testid="bom-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="BOM notes..."
                  className="rounded-sm"
                  data-testid="bom-notes-input"
                />
              </div>

              {/* BOM Lines Section */}
              <div className="border border-slate-200 rounded-sm p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">BOM Components</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setScannerOpen(true)}
                    className="rounded-sm"
                    data-testid="scan-component-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Scan Item
                  </Button>
                </div>
                
                {/* Add Line Form */}
                <div className="grid grid-cols-5 gap-2 items-end bg-slate-50 p-3 rounded-sm">
                  <div className="col-span-2">
                    <Label className="text-xs">Component</Label>
                    <Select value={newLine.component_sku} onValueChange={(v) => setNewLine({ ...newLine, component_sku: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="bom-line-component-select">
                        <SelectValue placeholder="Select item" />
                      </SelectTrigger>
                      <SelectContent>
                        {items.map(item => (
                          <SelectItem key={item.item_sku} value={item.item_sku}>
                            {item.item_sku} - {item.item_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Qty/Unit</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newLine.quantity_per_unit}
                      onChange={(e) => setNewLine({ ...newLine, quantity_per_unit: e.target.value })}
                      className="rounded-sm mono"
                      data-testid="bom-line-qty-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">UOM</Label>
                    <Input
                      value={newLine.uom}
                      onChange={(e) => setNewLine({ ...newLine, uom: e.target.value })}
                      className="rounded-sm"
                      data-testid="bom-line-uom-input"
                    />
                  </div>
                  <Button type="button" onClick={addLine} className="rounded-sm" data-testid="bom-add-line-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Lines List */}
                {formData.lines.length > 0 && (
                  <div className="border border-slate-200 rounded-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Component</TableHead>
                          <TableHead>Qty</TableHead>
                          <TableHead>UOM</TableHead>
                          <TableHead>Scrap %</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.lines.map((line, idx) => (
                          <TableRow key={line.id || idx}>
                            <TableCell className="mono text-sm">{line.component_sku}</TableCell>
                            <TableCell className="mono">{line.quantity_per_unit}</TableCell>
                            <TableCell>{line.uom}</TableCell>
                            <TableCell className="mono">{line.scrap_factor_percent}%</TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeLine(line.id)}
                                className="text-red-600"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bom-submit-btn">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search BOMs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-sm"
              data-testid="bom-search-input"
            />
          </div>
          <label className="text-xs text-slate-600 flex items-center gap-2 ml-3 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="bom-show-archived-toggle"
              className="rounded-sm"
            />
            Show archived
          </label>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="spinner" />
            </div>
          ) : filteredItems.length > 0 ? (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Product Type</TableHead>
                  <TableHead>Variant Code</TableHead>
                  <TableHead>Revision</TableHead>
                  <TableHead>Components</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id} data-testid={`bom-row-${item.id}`} className={item.archived ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">{getProductTypeName(item.product_type_id)}</TableCell>
                    <TableCell className="mono">
                      <button
                        type="button"
                        onClick={() => navigate(`/boms/view/${item.id}`)}
                        className="text-orange-600 hover:underline font-medium"
                        data-testid={`bom-variant-link-${item.id}`}
                      >
                        {item.bom_variant_code}
                      </button>
                    </TableCell>
                    <TableCell className="mono">{item.bom_revision}</TableCell>
                    <TableCell className="mono">{item.lines?.length || 0}</TableCell>
                    <TableCell>
                      <span className={`status-badge status-${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                      {item.rd_rework && (
                        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200" data-testid={`bom-rework-tag-${item.id}`}>In R&D Rework</span>
                      )}
                      {item.archived && (
                        <span className="ml-1 text-xs text-slate-400 inline-flex items-center gap-1" data-testid={`bom-archived-tag-${item.id}`}>
                          <Archive className="w-3 h-3" /> archived
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/boms/view/${item.id}`)}
                          title="Open BOM summary"
                          data-testid={`view-bom-${item.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {item.status === 'Draft' && hasRole(['Admin', 'ProductionSupervisor']) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRelease(item.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid={`release-bom-${item.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {item.status !== 'Obsolete' && hasRole(['Admin', 'ProductionSupervisor']) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setReviseTarget(item); setReviseNote(''); }}
                            className="text-orange-600 hover:bg-orange-50"
                            title="Revise — copies this BOM with the next revision number and obsoletes the current one"
                            data-testid={`revise-bom-${item.id}`}
                          >
                            <GitBranch className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-bom-${item.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {item.archived ? (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleUnarchive(item)}
                            className="text-slate-700 hover:bg-slate-100"
                            title="Restore from archive"
                            data-testid={`unarchive-bom-${item.id}`}
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleArchive(item)}
                            className="text-slate-500 hover:bg-slate-100"
                            title="Archive — hides from default list"
                            data-testid={`archive-bom-${item.id}`}
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        {item.status !== 'Released' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-bom-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 text-slate-500">
              <ClipboardList className="w-12 h-12 mb-3 text-slate-300" />
              <p>No BOMs found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revise dialog */}
      <Dialog
        open={!!reviseTarget}
        onOpenChange={(o) => { if (!o) { setReviseTarget(null); setReviseNote(''); } }}
      >
        <DialogContent className="sm:max-w-md" data-testid="revise-bom-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-orange-600" /> Revise BOM
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Creates a new <span className="font-medium">Draft</span> BOM by copying every line from{' '}
              <span className="mono font-medium">{reviseTarget?.bom_variant_code} rev {reviseTarget?.bom_revision}</span>{' '}
              and bumps the revision automatically. The current version is marked <span className="font-medium">Obsolete</span> and archived.
            </p>
            <div className="space-y-2">
              <Label>Notes for the new revision <span className="text-xs text-slate-400">(optional)</span></Label>
              <Textarea
                value={reviseNote}
                onChange={(e) => setReviseNote(e.target.value)}
                placeholder="What changed in this revision?"
                className="rounded-sm"
                data-testid="revise-bom-note-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="outline" onClick={() => setReviseTarget(null)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handleRevise}
              disabled={reviseBusy}
              className="bg-orange-600 hover:bg-orange-700 rounded-sm"
              data-testid="revise-bom-confirm-btn"
            >
              <GitBranch className="w-4 h-4 mr-1" /> {reviseBusy ? 'Revising…' : 'Create new revision'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleQRScan}
        title="Scan Component QR Code"
      />
    </div>
  );
};

export default BOMsPage;
